'use strict';
import { getPaintOrder, MAX_Z_INDEX } from './layers.js';
import { sanitizeIcon } from './icon-assets.js';
import { isHexColor, normalizeNodeAppearance, NODE_APPEARANCE_DEFAULTS } from './appearance.js';
const copy = value => JSON.parse(JSON.stringify(value));
const HEADER = 40;
const DEFAULTS = {
  parentId: null, description: '', icon: 'box', widthMode: 'fixed', heightMode: 'fixed',
  layout: 'free', padding: 20, gap: 16, textAlign: 'left', verticalAlign: 'center',
  textOverflow: 'ellipsis', overflow: 'ellipsis', iconPosition: 'left', color: 'blue', ...NODE_APPEARANCE_DEFAULTS
};
const fail = message => { throw new Error(message); };
const finite = value => typeof value === 'number' && Number.isFinite(value);
const COORDINATE_LIMIT = 10000000;
const bounded = value => finite(value) && Math.abs(value) <= COORDINATE_LIMIT;
const pointValue = value => value && typeof value === 'object' && !Array.isArray(value) && bounded(value.x) && bounded(value.y);
const incoming = value => JSON.parse(JSON.stringify(value), (key, item) => {
  if (['__proto__', 'prototype', 'constructor'].includes(key)) fail('数据包含不支持的对象字段。');
  return item;
});
const record = value => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) fail('修改内容必须是有效对象。');
  return incoming(value);
};
const diagram = state => JSON.stringify({ name: state.name, nodes: state.nodes, edges: state.edges });
const documentName = value => {
  if (typeof value !== 'string') fail('文档名称必须是文字。');
  const name = value.trim();
  if (!name) fail('请输入文档名称。');
  if (Array.from(name).length > 60) fail('文档名称最多为 60 个字符。');
  return name;
};
const uuid = value => typeof value === 'string' && /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i.test(value);
const shortText = (value, max) => typeof value === 'string' && value.trim().length > 0 && Array.from(value.trim()).length <= max;

function normalizeIconAsset(node) {
  const custom = typeof node.icon === 'string' && node.icon.startsWith('asset:');
  if (node.iconAsset == null) {
    if (custom) fail('自定义图标缺少可保存的图片资源。');
    return;
  }
  const asset = node.iconAsset;
  if (typeof asset !== 'object' || Array.isArray(asset) || !uuid(asset.id) || !custom || node.icon !== `asset:${asset.id}`) fail('自定义图标与资源身份不匹配。');
  if (!shortText(asset.name, 100) || !asset.author || typeof asset.author !== 'object' || Array.isArray(asset.author) || !uuid(asset.author.id) || !shortText(asset.author.name, 60)) fail('自定义图标名称或作者信息无效。');
  if (!['upload', 'ai'].includes(asset.source)) fail('自定义图标来源必须是上传或 AI 生成。');
  node.iconAsset = { ...asset, name: asset.name.trim(), author: { id: asset.author.id, name: asset.author.name.trim() }, ...sanitizeIcon(asset) };
}

class Graph {
  constructor(initial) {
    this.state = { name: '电商系统架构', nodes: [], edges: [], selectedId: null };
    this._undo = [];
    this._redo = [];
    if (initial) {
      const result = this.load(initial);
      if (!result.ok) throw new Error(result.error);
    } else {
      this.state = {
        name: '电商系统架构',
        nodes: [
          this._node('container', { id: 'production', x: 40, y: 32, title: '生产环境', icon: 'cloud', color: 'slate' }),
          this._node('container', { id: 'business', parentId: 'production', x: 80, y: 100, title: '业务系统', icon: 'layers', color: 'blue' }),
          this._node('service', { id: 'users', parentId: 'business', x: 104, y: 164, title: '用户服务', description: '账户与用户资料', icon: 'users' }),
          this._node('service', { id: 'auth', parentId: 'business', x: 284, y: 164, title: '认证服务', description: '统一身份认证', icon: 'shield-check' }),
          this._node('service', { id: 'orders', x: 600, y: 164, title: '订单服务', description: '试着把我拖入业务系统', icon: 'shopping-bag', color: 'orange' })
        ],
        edges: [
          { id: 'edge-orders-users', source: 'orders', target: 'users', dashed: true, label: '查询用户', arrow: true },
          { id: 'edge-auth-users', source: 'auth', target: 'users', dashed: false, label: '', arrow: true }
        ],
        selectedId: 'business'
      };
      this._normalize();
    }
  }
  _node(type, opts) {
    const container = type === 'container';
    const node = Object.assign({}, DEFAULTS, {
      type: container ? 'container' : 'service', x: 80, y: 80,
      w: container ? 240 : 160, h: container ? 180 : 72,
      title: container ? '新区域' : '新服务',
      minW: container ? 200 : 120, minH: container ? 160 : 72,
      widthMode: container ? 'auto' : 'fixed', heightMode: container ? 'auto' : 'fixed'
    }, record(opts || {}));
    if (container) {
      if (!Object.prototype.hasOwnProperty.call(node, 'anchorX')) node.anchorX = node.x;
      if (!Object.prototype.hasOwnProperty.call(node, 'anchorY')) node.anchorY = node.y;
    }
    return node;
  }
  get selectedId() { return this.state.selectedId; }
  get selected() { return this.getNode(this.selectedId) || this.getEdge(this.selectedId) || null; }
  get canUndo() { return this._undo.length > 0; }
  get canRedo() { return this._redo.length > 0; }
  getNode(id) { return this.state.nodes.find(node => node.id === id) || null; }
  getEdge(id) { return this.state.edges.find(edge => edge.id === id) || null; }
  children(id) { return this.state.nodes.filter(node => node.parentId === id); }
  descendants(id) {
    const result = [], visited = new Set([id]), visit = parent => {
      this.children(parent).forEach(node => {
        if (visited.has(node.id)) return;
        visited.add(node.id); result.push(node); visit(node.id);
      });
    };
    visit(id); return result;
  }
  snapshot() { return copy(this.state); }
  serialize() { return JSON.stringify(Object.assign({ version: 1 }, this.snapshot()), null, 2); }
  select(id) {
    if (id !== null && !this.getNode(id) && !this.getEdge(id)) return { ok: false, error: '找不到选中对象。' };
    this.state.selectedId = id; return { ok: true };
  }
  _id(prefix) {
    let index = 1;
    while (this.getNode(prefix + index) || this.getEdge(prefix + index)) index++;
    return prefix + index;
  }
  _frontZIndex() {
    const order = getPaintOrder(this.state);
    if (!order.length) return 0;
    const highest = Math.max(...order.map(entry => entry.item.zIndex));
    if (highest < MAX_Z_INDEX) return highest + 1;
    // Preserve effective order and only renumber when the integer range is full.
    order.forEach((entry, index) => { entry.item.zIndex = index; });
    return order.length;
  }
  _commit(action) {
    const before = this.snapshot();
    try {
      const result = action() || {};
      this._normalize();
      if (diagram(before) !== diagram(this.state)) {
        this._undo.push(before);
        if (this._undo.length > 80) this._undo.shift();
        this._redo = [];
      }
      return Object.assign({ ok: true }, result);
    } catch (error) {
      this.state = before;
      return { ok: false, error: error.message || '操作无法完成。' };
    }
  }
  _translate(node, dx, dy) {
    if (dx === 0 && dy === 0) return;
    const moving = [node].concat(this.descendants(node.id)), ids = new Set(moving.map(item => item.id));
    moving.forEach(item => {
      item.x += dx; item.y += dy;
      if (item.type === 'container') { item.anchorX += dx; item.anchorY += dy; }
    });
    this.state.edges.forEach(edge => {
      if (ids.has(edge.source) && ids.has(edge.target) && Array.isArray(edge.waypoints)) edge.waypoints = edge.waypoints.map(point => ({ ...point, x: point.x + dx, y: point.y + dy }));
    });
  }
  _depth(node) {
    let depth = 0, parent = node.parentId;
    const visited = new Set([node.id]);
    while (parent) {
      if (visited.has(parent)) fail('区域不能放入自己或自己的子区域。');
      visited.add(parent);
      const next = this.getNode(parent);
      if (!next) fail('父区域不存在。');
      parent = next.parentId; depth++;
      if (depth > 64) fail('区域最多支持 64 层嵌套。');
    }
    return depth;
  }
  _normalize() {
    this.state.name = documentName(this.state.name);
    if (this.state.nodes.length > 1000 || this.state.edges.length > 5000) fail('单份文档最多支持 1000 个节点和 5000 条连线。');
    const ids = new Set();
    this.state.nodes.forEach(node => {
      if (!node.id || typeof node.id !== 'string' || ids.has(node.id)) fail('节点身份重复或无效。');
      ids.add(node.id);
      if (!['container', 'service'].includes(node.type)) fail('不支持的节点类型。');
      if (![node.x, node.y, node.w, node.h, node.x + node.w, node.y + node.h].every(bounded)) fail('位置和尺寸必须是绝对值不超过 10000000 的有效数字。');
      if (node.type === 'container' && ![node.anchorX, node.anchorY].every(bounded)) fail('区域位置锚点无效。');
      if (node.w < (node.type === 'container' ? 200 : 80) || node.h < (node.type === 'container' ? 160 : 40)) fail('区域或节点的尺寸过小。');
      if (!['auto', 'fixed'].includes(node.widthMode) || !['auto', 'fixed'].includes(node.heightMode)) fail('尺寸策略无效。');
      if (!['free', 'vertical'].includes(node.layout)) fail('暂不支持这种布局方式。');
      if (node.layoutDirection != null && !['grid', 'vertical', 'horizontal'].includes(node.layoutDirection)) fail('内容整理方向无效。');
      if (!bounded(node.padding) || node.padding < 0 || !bounded(node.gap) || node.gap < 0) fail('内边距和间距必须是范围内的非负数字。');
      if (!bounded(node.minW) || node.minW < 0 || !bounded(node.minH) || node.minH < 0) fail('最小尺寸必须是范围内的有效数字。');
      node.title = String(node.title == null ? '' : node.title);
      node.description = String(node.description == null ? '' : node.description);
      normalizeNodeAppearance(node);
      normalizeIconAsset(node);
      if (node.parentId !== null && typeof node.parentId !== 'string') fail('父区域身份无效。');
      if (node.parentId) {
        const parent = this.getNode(node.parentId);
        if (!parent || parent.type !== 'container') fail('只能放入可容纳内容的区域。');
      }
      const depth = this._depth(node);
      if (node.zIndex === undefined) node.zIndex = node.type === 'container' ? depth : 100 + depth;
      if (!Number.isInteger(node.zIndex) || Math.abs(node.zIndex) > MAX_Z_INDEX) fail('节点图层顺序必须是 -1000000 到 1000000 之间的整数。');
    });
    this.state.nodes.filter(node => node.type === 'service').forEach(node => this._sizeService(node));
    this.state.edges.forEach(edge => {
      if (!edge || typeof edge.id !== 'string' || !edge.id || ids.has(edge.id)) fail('连线身份重复或无效。');
      ids.add(edge.id);
      if (!this.getNode(edge.source) || !this.getNode(edge.target)) fail('连线引用的节点不存在。');
      if (edge.source === edge.target) fail('暂不支持连接到节点自身。');
      if (edge.strokeColor != null && !isHexColor(edge.strokeColor)) delete edge.strokeColor;
      if (edge.zIndex === undefined) edge.zIndex = 80;
      if (!Number.isInteger(edge.zIndex) || Math.abs(edge.zIndex) > MAX_Z_INDEX) fail('连线图层顺序必须是 -1000000 到 1000000 之间的整数。');
      for (const name of ['sourceAnchor', 'targetAnchor']) {
        const anchor = edge[name];
        if (anchor == null) continue;
        if (typeof anchor !== 'object' || Array.isArray(anchor)) fail('连线端点锚定设置无效。');
        if (anchor.side !== undefined && !['left', 'right', 'top', 'bottom'].includes(anchor.side)) fail('连线端点方向无效。');
        if (anchor.offset !== undefined && (!finite(anchor.offset) || anchor.offset < 0 || anchor.offset > 1)) fail('连线端点偏移必须在 0 到 1 之间。');
      }
      if (edge.waypoints != null && (!Array.isArray(edge.waypoints) || edge.waypoints.length > 100 || !edge.waypoints.every(pointValue))) fail('连线最多支持 100 个坐标有效的手动控制点。');
      if (edge.labelOffset != null && !pointValue(edge.labelOffset)) fail('连线文字偏移必须包含范围内的有效 x、y 坐标。');
      edge.label = String(edge.label == null ? '' : edge.label);
    });
    const containers = this.state.nodes.filter(node => node.type === 'container');
    // Repeated passes account for child sizing before ancestor layout, without changing world positions in free layout.
    for (let pass = 0; pass < 2; pass++) {
      containers.slice().sort((a, b) => this._depth(b) - this._depth(a)).forEach(parent => this._fit(parent));
    }
    this.state.nodes.forEach(node => {
      if (![node.x, node.y, node.w, node.h, node.x + node.w, node.y + node.h].every(bounded)) fail('布局结果超出可处理范围。');
    });
    this.state.edges.forEach(edge => {
      if (Array.isArray(edge.waypoints) && !edge.waypoints.every(pointValue)) fail('连线控制点移动后超出可处理范围。');
    });
    if (this.state.selectedId && !ids.has(this.state.selectedId)) this.state.selectedId = null;
  }
  _sizeService(node) {
    // Deterministic estimate shared by server and browser; SVG uses the same metrics.
    const measure = text => Array.from(text).reduce((sum, char) => sum + (/[^\x00-\xff]/.test(char) ? 14 : 7), 0);
    const titleWidth = Math.max(...node.title.split('\n').map(measure), 0);
    const bodyWidth = Math.max(...node.description.split('\n').map(measure), 0);
    const inset = node.iconPosition === 'top' ? 32 : 64;
    if (node.widthMode === 'auto') node.w = Math.max(80, node.minW, Math.min(380, Math.max(titleWidth, bodyWidth) + inset));
    if (node.heightMode === 'auto') {
      const available = Math.max(32, node.w - inset);
      const lines = text => text ? text.split('\n').reduce((sum, line) => sum + Math.max(1, Math.ceil(measure(line) / available)), 0) : 0;
      node.h = Math.max(40, node.minH, 24 + lines(node.title) * 20 + lines(node.description) * 18 + (node.iconPosition === 'top' ? 28 : 0));
    }
  }
  _fit(parent) {
    const children = this.children(parent.id), pad = parent.padding;
    if (parent.layout === 'vertical') {
      let y = parent.y + HEADER + pad;
      children.forEach(child => {
        this._translate(child, parent.x + pad - child.x, y - child.y);
        y += child.h + parent.gap;
      });
    }
    if (children.length) {
      const minX = Math.min(...children.map(node => node.x));
      const minY = Math.min(...children.map(node => node.y));
      if (parent.widthMode === 'auto') parent.x = Math.min(parent.anchorX, minX - pad);
      if (parent.heightMode === 'auto') parent.y = Math.min(parent.anchorY, minY - HEADER - pad);
    } else {
      if (parent.widthMode === 'auto') parent.x = parent.anchorX;
      if (parent.heightMode === 'auto') parent.y = parent.anchorY;
    }
    const right = children.length ? Math.max(...children.map(node => node.x + node.w)) + pad : parent.x + parent.minW;
    const bottom = children.length ? Math.max(...children.map(node => node.y + node.h)) + pad : parent.y + parent.minH;
    if (parent.widthMode === 'auto') parent.w = Math.max(200, parent.minW, right - parent.x);
    if (parent.heightMode === 'auto') parent.h = Math.max(160, parent.minH, bottom - parent.y);
    const badWidth = children.some(child => child.x < parent.x + pad - .01 || child.x + child.w > parent.x + parent.w - pad + .01);
    const badHeight = children.some(child => child.y < parent.y + HEADER + pad - .01 || child.y + child.h > parent.y + parent.h - pad + .01);
    if (parent.widthMode === 'fixed' && badWidth) fail('“' + parent.title + '”的固定宽度放不下内容，请增大宽度或切换为自动。');
    if (parent.heightMode === 'fixed' && badHeight) fail('“' + parent.title + '”的固定高度放不下内容，请增大高度或切换为自动。');
  }
  addNode(type, opts, options = {}) {
    return this._commit(() => {
      if (!['container', 'service'].includes(type)) fail('不支持的节点类型。');
      const settings = record(opts || {});
      const parent = options?.free === true && settings.parentId ? this.getNode(settings.parentId) : null;
      if (parent?.type === 'container' && parent.layout === 'vertical') parent.layout = 'free';
      if (!Object.prototype.hasOwnProperty.call(settings, 'zIndex')) settings.zIndex = this._frontZIndex();
      const node = this._node(type, Object.assign({ id: this._id(type === 'container' ? 'region-' : 'service-') }, settings));
      this.state.nodes.push(node); this.state.selectedId = node.id;
      return { id: node.id };
    });
  }
  updateNode(id, patch) {
    return this._commit(() => {
      const node = this.getNode(id);
      if (!node) fail('找不到要修改的节点。');
      patch = record(patch);
      if (patch.id && patch.id !== id) fail('不能修改节点身份。');
      if (Object.prototype.hasOwnProperty.call(patch, 'parentId') && patch.parentId !== node.parentId) fail('请通过拖动来修改节点归属。');
      const safe = Object.assign({}, patch);
      delete safe.id; delete safe.type; delete safe.parentId;
      if ('w' in safe && !('widthMode' in safe)) safe.widthMode = 'fixed';
      if ('h' in safe && !('heightMode' in safe)) safe.heightMode = 'fixed';
      const dx = 'x' in safe ? safe.x - node.x : 0, dy = 'y' in safe ? safe.y - node.y : 0;
      delete safe.x; delete safe.y;
      this._translate(node, dx, dy);
      Object.assign(node, safe);
      return { id };
    });
  }
  moveNode(id, x, y, targetParentId, options) {
    return this._commit(() => {
      const node = this.getNode(id);
      if (!node) fail('找不到要移动的节点。');
      if (!finite(x) || !finite(y)) fail('落点无效。');
      const keep = options && (options.preserveParent || options.keepParent);
      const parentId = keep || targetParentId === undefined ? node.parentId : targetParentId;
      if (parentId === id || this.descendants(id).some(child => child.id === parentId)) fail('区域不能放入自己或自己的子区域。');
      if (parentId) {
        const parent = this.getNode(parentId);
        if (!parent || parent.type !== 'container') fail('目标不能容纳子区域。');
        const moved = x !== node.x || y !== node.y || parentId !== node.parentId;
        if (options?.free && moved && parent.layout === 'vertical') parent.layout = 'free';
      }
      this._translate(node, x - node.x, y - node.y);
      node.parentId = parentId || null;
      if (options && Number.isInteger(options.index) && parentId) {
        const other = this.children(parentId).filter(child => child.id !== id);
        const insertion = Math.max(0, Math.min(options.index, other.length));
        this.state.nodes = this.state.nodes.filter(item => item.id !== id);
        const anchor = other[insertion];
        const pos = anchor ? this.state.nodes.findIndex(item => item.id === anchor.id) : this.state.nodes.length;
        this.state.nodes.splice(pos, 0, node);
      }
      this.state.selectedId = id;
      return { id };
    });
  }
  _outlinePaint(id, targetId, position) {
    const order = getPaintOrder(this.state), index = order.findIndex(entry => entry.item.id === id);
    const selected = order[index], original = order.map(entry => ({ item: entry.item, zIndex: entry.item.zIndex }));
    const desired = order.slice(); desired.splice(index, 1);
    if (position === 'inside') desired.push(selected);
    else {
      const target = desired.findIndex(entry => entry.item.id === targetId);
      // The outline displays front-most siblings first, opposite paint order.
      desired.splice(target + (position === 'before' ? 1 : 0), 0, selected);
    }
    desired.forEach((entry, rank) => { entry.item.zIndex = rank; });
    const next = getPaintOrder(this.state);
    const changed = next.some((entry, rank) => entry.item.id !== order[rank].item.id);
    if (!changed) original.forEach(entry => { entry.item.zIndex = entry.zIndex; });
    return changed;
  }
  moveInOutline(id, targetId, position) {
    return this._commit(() => {
      if (!['before', 'after', 'inside', 'root'].includes(position)) fail('请选择有效的结构移动方式。');
      const node = this.getNode(id), edge = this.getEdge(id);
      if (!node && !edge) fail('找不到要移动的对象。');
      const targetNode = position === 'root' ? null : this.getNode(targetId);
      const targetEdge = position === 'root' ? null : this.getEdge(targetId);
      if (position !== 'root' && !targetNode && !targetEdge) fail('找不到目标对象。');
      if (position === 'inside' && (!node || targetNode?.type !== 'container')) fail('只能将节点放入可容纳内容的区域。');
      this.state.selectedId = id;
      if (id === targetId && (position === 'before' || position === 'after')) return { id, changed: false };
      if (edge && position === 'root') return { id, changed: false };
      let changed = false;
      if (node) {
        const parentId = position === 'root' ? null : position === 'inside' ? targetId : targetNode?.parentId || null;
        if (parentId === id || this.descendants(id).some(child => child.id === parentId)) fail('区域不能放入自己或自己的子区域。');
        if (parentId !== node.parentId) {
          if (parentId) {
            const parent = this.getNode(parentId), left = parent.x + parent.padding, top = parent.y + HEADER + parent.padding;
            const width = parent.w - 2 * parent.padding, height = parent.h - HEADER - 2 * parent.padding;
            if (parent.widthMode === 'fixed' && node.w > width + .01) fail('“' + parent.title + '”的固定宽度放不下内容，请增大宽度或切换为自动。');
            if (parent.heightMode === 'fixed' && node.h > height + .01) fail('“' + parent.title + '”的固定高度放不下内容，请增大高度或切换为自动。');
            const x = Math.min(Math.max(node.x, left), Math.max(left, left + width - node.w));
            const y = Math.min(Math.max(node.y, top), Math.max(top, top + height - node.h));
            // Moving in the outline has no canvas drop point. Keep coordinates
            // when visible, otherwise minimally translate the complete subtree.
            this._translate(node, x - node.x, y - node.y);
            if (parent.layout === 'vertical') parent.layout = 'free';
          }
          node.parentId = parentId; changed = true;
        }
      }
      if (position !== 'root') changed = this._outlinePaint(id, targetId, position) || changed;
      return { id, changed };
    });
  }
  layoutChildren(id, options = {}) {
    return this._commit(() => {
      const parent = this.getNode(id), settings = record(options);
      if (!parent || parent.type !== 'container') fail('请选择一个可容纳内容的区域。');
      const direction = settings.direction ?? 'grid';
      if (!['grid', 'vertical', 'horizontal'].includes(direction)) fail('请选择网格、纵向或横向整理。');
      const gap = settings.gap ?? parent.gap;
      if (!bounded(gap) || gap < 0) fail('整理间距必须是范围内的非负数字。');
      const children = this.children(id).slice().sort((a, b) => a.y - b.y || a.x - b.x);
      if (!children.length) fail('区域内没有可整理的内容。');
      parent.layout = 'free'; parent.layoutDirection = direction; parent.gap = gap;
      const left = parent.x + parent.padding, top = parent.y + HEADER + parent.padding;
      if (direction === 'vertical') {
        let y = top;
        children.forEach(child => { this._translate(child, left - child.x, y - child.y); y += child.h + gap; });
      } else if (direction === 'horizontal') {
        let x = left;
        children.forEach(child => { this._translate(child, x - child.x, top - child.y); x += child.w + gap; });
      } else {
        const cellWidth = Math.max(...children.map(child => child.w));
        const columns = parent.widthMode === 'fixed'
          ? Math.max(1, Math.min(children.length, Math.floor((parent.w - 2 * parent.padding + gap) / (cellWidth + gap))))
          : Math.ceil(Math.sqrt(children.length));
        let y = top;
        for (let index = 0; index < children.length; index += columns) {
          const row = children.slice(index, index + columns);
          row.forEach((child, column) => this._translate(child, left + column * (cellWidth + gap) - child.x, y - child.y));
          y += Math.max(...row.map(child => child.h)) + gap;
        }
      }
      this.state.selectedId = id;
      return { id };
    });
  }
  removeNode(id) {
    return this._commit(() => {
      if (!this.getNode(id)) fail('找不到要删除的节点。');
      const removed = new Set([id].concat(this.descendants(id).map(node => node.id)));
      this.state.nodes = this.state.nodes.filter(node => !removed.has(node.id));
      this.state.edges = this.state.edges.filter(edge => !removed.has(edge.source) && !removed.has(edge.target));
      this.state.selectedId = null;
    });
  }
  addEdge(source, target, opts) {
    return this._commit(() => {
      if (!this.getNode(source) || !this.getNode(target)) fail('请连接两个有效节点。');
      if (source === target) fail('暂不支持连接到节点自身。');
      const edge = Object.assign({ id: this._id('edge-'), source, target, dashed: false, label: '', arrow: true }, record(opts || {}));
      this.state.edges.push(edge); this.state.selectedId = edge.id;
      return { id: edge.id };
    });
  }
  updateEdge(id, patch) {
    return this._commit(() => {
      const edge = this.getEdge(id);
      if (!edge) fail('找不到要修改的连线。');
      const safe = record(patch); delete safe.id;
      Object.assign(edge, safe); return { id };
    });
  }
  removeEdge(id) {
    return this._commit(() => {
      if (!this.getEdge(id)) fail('找不到要删除的连线。');
      this.state.edges = this.state.edges.filter(edge => edge.id !== id);
      if (this.state.selectedId === id) this.state.selectedId = null;
    });
  }
  reorderLayer(id, action) {
    return this._commit(() => {
      if (!['front', 'back', 'forward', 'backward'].includes(action)) fail('请选择有效的图层调整方式。');
      const order = getPaintOrder(this.state), index = order.findIndex(entry => entry.item.id === id);
      if (index === -1) fail('找不到要调整的图层。');
      const selected = order[index], related = new Set();
      if (selected.kind === 'node') {
        this.descendants(id).forEach(node => related.add(node.id));
        let parentId = selected.item.parentId;
        while (parentId) {
          related.add(parentId); parentId = this.getNode(parentId)?.parentId;
        }
      }
      let target;
      if (action === 'front') target = order.length - 1;
      else if (action === 'back') target = 0;
      else {
        const direction = action === 'forward' ? 1 : -1;
        target = index + direction;
        while (target >= 0 && target < order.length && order[target].kind === 'node' && related.has(order[target].item.id)) target += direction;
        if (target < 0 || target >= order.length) target = index;
      }
      this.state.selectedId = id;
      if (target === index) return { id, changed: false };
      const original = order.map(entry => ({ item: entry.item, zIndex: entry.item.zIndex }));
      const desired = order.slice(); desired.splice(index, 1); desired.splice(target, 0, selected);
      desired.forEach((entry, position) => { entry.item.zIndex = position; });
      const next = getPaintOrder(this.state);
      const changed = next.some((entry, position) => entry.item.id !== order[position].item.id);
      if (!changed) original.forEach(entry => { entry.item.zIndex = entry.zIndex; });
      return { id, changed };
    });
  }
  undo() {
    if (!this.canUndo) return { ok: false, error: '没有可撤销的操作。' };
    this._redo.push(this.snapshot()); this.state = this._undo.pop(); return { ok: true };
  }
  redo() {
    if (!this.canRedo) return { ok: false, error: '没有可重做的操作。' };
    this._undo.push(this.snapshot()); this.state = this._redo.pop(); return { ok: true };
  }
  rename(name) {
    return this._commit(() => { this.state.name = documentName(name); });
  }
  _read(snapshot) {
    if (typeof snapshot === 'string' && snapshot.length > 2 * 1024 * 1024) fail('单个图文件不能超过 2 MB。');
    let parsed = record(typeof snapshot === 'string' ? JSON.parse(snapshot) : snapshot);
    if (parsed.state) parsed = parsed.state;
    if (!Array.isArray(parsed.nodes) || !Array.isArray(parsed.edges)) fail('文件中没有有效的图数据。');
    if (parsed.nodes.length > 1000 || parsed.edges.length > 5000) fail('单份文档最多支持 1000 个节点和 5000 条连线。');
    if (parsed.nodes.some(node => !node || typeof node.id !== 'string' || !['container', 'service'].includes(node.type) || ![node.x, node.y, node.w, node.h].every(finite))) fail('文档中的节点数据不完整。');
    return {
      name: documentName(parsed.name === undefined ? '未命名架构图' : parsed.name),
      nodes: parsed.nodes.map(node => this._node(node.type, node)),
      edges: copy(parsed.edges), selectedId: parsed.selectedId || null
    };
  }
  replace(snapshot) {
    return this._commit(() => { this.state = this._read(snapshot); });
  }
  clear() { return this.replace({ name: '未命名架构图', nodes: [], edges: [], selectedId: null }); }
  load(snapshot) {
    const before = this.snapshot();
    try {
      this.state = this._read(snapshot);
      this._normalize(); this._undo = []; this._redo = [];
      return { ok: true };
    } catch (error) {
      this.state = before; return { ok: false, error: error.message || '文档无法读取。' };
    }
  }
}

function normalizeGraph(snapshot) {
  const graph = new Graph();
  const result = graph.load(snapshot);
  if (!result.ok) throw new Error(result.error);
  return graph.snapshot();
}

function validateGraph(snapshot) {
  try { return { ok: true, graph: normalizeGraph(snapshot) }; }
  catch (error) { return { ok: false, error: error.message || '图文档无效。' }; }
}

export { Graph, HEADER, normalizeGraph, validateGraph };
